import { useState } from 'react'

import { useToasts } from '@/hooks/useToasts'

import { AssociateDeviceI } from '@/features/floor-plan'
import { VideoDimensionsIProps } from '@/features/video-player'
import {
  useDeleteTailgateGridMutation,
  useUpdateTailgateGridMutation,
} from '@/graphql/generated/hooks'
import {
  DeleteTailgateGridInput,
  UpdateCameraInput,
  UpdateTailgateGridInput,
} from '@/graphql/generated/schemas'

import { TailgateBox } from '../types/types'
import { getBoxInput } from '../utils/utils'

interface UseUpdateTailgateGridIProps {
  associatedDevices: AssociateDeviceI | null
  onClose: () => void
  onSaveCallback?: () => void
}

export const useUpdateTailgateGrid = ({
  associatedDevices,
  onClose,
  onSaveCallback,
}: UseUpdateTailgateGridIProps) => {
  const [isLoading, setLoading] = useState(false)

  const { showSuccess, showError } = useToasts({ toastDuration: 3000 })

  const [updateTailgateGrid] = useUpdateTailgateGridMutation()
  const [deleteTailgateGrid] = useDeleteTailgateGridMutation()

  const updateGrid = async (
    inBox: TailgateBox,
    outBox: TailgateBox,
    videoDimensions: VideoDimensionsIProps
  ) => {
    if (!associatedDevices) return
    const { cameraId, doorId, cameraName, doorName } = associatedDevices

    setLoading(true)

    const updateTailgateGridInput: UpdateTailgateGridInput = {
      cameraId,
      doorId,
      inBox: getBoxInput(inBox),
      outBox: getBoxInput(outBox),
    }
    // Camera streaming resolution should be updated on each tailgating grid update
    const updateCameraInput: UpdateCameraInput = {
      id: cameraId,
      streamingResolutionHeight: videoDimensions.height,
      streamingResolutionWidth: videoDimensions.width,
    }

    try {
      await updateTailgateGrid({
        variables: { updateTailgateGridInput, updateCameraInput },
        refetchQueries: ['FacilityDevicesByFloors', 'TailgateGrid'],
      })
      showSuccess(
        `Successfully edited Tailgate Grid for Associated Device ${cameraName} and ${doorName}`
      )
      onClose()
      onSaveCallback?.()
    } catch {
      showError('Error editing Tailgate Grid')
    }
    setLoading(false)
  }

  const deleteGrid = async () => {
    if (!associatedDevices) return
    const { cameraId, doorId, cameraName, doorName } = associatedDevices

    setLoading(true)
    const input: DeleteTailgateGridInput = { cameraId, doorId }
    try {
      await deleteTailgateGrid({
        variables: { input },
        refetchQueries: ['FacilityDevicesByFloors'],
      })
      showSuccess(
        `Successfully removed Tailgate Grid for Associated Device ${cameraName} and ${doorName}`
      )
      onClose()
      onSaveCallback?.()
    } catch {
      showError('Error removing Tailgate Grid')
    }
    setLoading(false)
  }

  return {
    isUpdateLoading: isLoading,
    updateGrid,
    deleteGrid,
  }
}
